import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, Paper, Stack, Typography } from "@mui/material";
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { debuglog } from '../Log';
import { useFeedbackContext } from '../context/FeedbackContext';
import { uploadPocketHTMLfileForImport } from '../ApiClient';
import { useUserContext } from '../context/UserContext';
import { useNavigate } from 'react-router';

interface UploadDialogProps {
  open: boolean;
}

export const UploadDialog: React.FC<UploadDialogProps> = ({ open }) => {


  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useFeedbackContext();
  const { user } = useUserContext();

  const [isUploading, setIsUploading] = React.useState<boolean>(false);

  const onDrop = (acceptedFiles: File[]) => {

    debuglog('uploading', acceptedFiles);
    const fileToUpload = acceptedFiles[0];
    const reader = new FileReader();
    reader.onabort = () => {
      console.error('file reading was aborted');
      showErrorMessage('File reading was aborted');
    }
    reader.onerror = () => {
      console.error('file reading has failed');
      showErrorMessage('File reading has failed');
    }
    reader.onload = async () => {
      if (user) {
        setIsUploading(true);
        const binaryStr = reader.result
        const uint8Array = new Uint8Array(binaryStr as ArrayBuffer);
        const decoder = new TextDecoder('utf-8');
        const decoded = decoder.decode(uint8Array);
        const response = await uploadPocketHTMLfileForImport(user, decoded);
        if (response.result) {
          showMessage(`Imported ${response.result.successCount} bookmarks with ${response.result.failureCount} failures. ${response.result.alreadyExistsCount} bookmarks already existed.}`);
          close();
        }
        else {
          showErrorMessage('Failed to import bookmarks');
        }
        setIsUploading(false);
      }
    }
    reader.readAsArrayBuffer(fileToUpload);
  }

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <Dialog open={open} onClose={close} fullWidth>
      <Stack spacing={2}>
        <DialogTitle>Import from Pocket</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              This will import your bookmarks from a Pocket HTML export file.
              You can export your bookmarks from Pocket by following <Link href="https://getpocket.com/export" target="_blank" rel="noreferrer">this link</Link>
            </DialogContentText>
            <Paper {...getRootProps()} sx={{
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'text.secondary',
              },
            }} elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 1, minHeight: '144px' }}>
                <input {...getInputProps()} />
                {!isUploading && (
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                )}
                {isUploading && <CircularProgress size={48} />}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: 1 }}>
                <Typography textAlign='center'>
                  Drag and drop your Pocket HTML export file here,<br /> or click to open a file selection dialog
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={()=>navigate('/')}
            variant='outlined'
          >Close</Button>
        </DialogActions>
      </Stack >
    </Dialog >
  );
};
